import { makeStyles } from "@material-ui/core";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import ByPersonTabComponent from "./monthly-tm-hours/ByPerson";
import ByLcat from "./monthly-tm-hours/ByLcat";
import ByPOPTabComponent from "./monthly-tm-hours/ByPOP";
import Loading from "../../../common-components/Loading";
import {
  downloadMonthlyTMByPersonAPI,
  downloadMonthlyTMByLcatAPI,
  downloadMonthlyTMByPOPAPI,
} from "../../../api/reports-api";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";

import {
  TH_BG,
  TH_BG_BAND,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../reportTableConfig";

const NAV = "#0D2137";
const HEADER_BG = TH_BG_BAND;
const HEADER_FC = TH_BG; /* slightly darker for Forecast cols */
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

/* ══════════════════════════════════════════════════════════════ */
/*  Tab definitions                                               */
/* ══════════════════════════════════════════════════════════════ */
const TABS = [
  { key: "by_person", label: "By Person" },
  { key: "by_lcat", label: "By LCAT" },
  { key: "by_pop", label: "By Period of Performance" },
];

const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TAB_FILTERS = {
  by_person: [
    "year",
    "contractId",
    "quarter",
    "monthName",
    "projectId",
    "bu",
    "employeeId",
  ],
  by_lcat: [
    "year",
    "contractId",
    "quarter",
    "monthName",
    "projectId",
    "bu",
    "employeeId",
  ],
  by_pop: [
    "year",
    "contractId",
    "quarter",
    "monthName",
    "projectId",
    "bu",
    "employeeId",
  ],
};

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

/* Per-tab download endpoint + output filename */
const DOWNLOAD_BY_TAB = {
  by_person: { api: downloadMonthlyTMByPersonAPI, filename: "monthly-tm-hours-by-person.xlsx" },
  by_lcat: { api: downloadMonthlyTMByLcatAPI, filename: "monthly-tm-hours-by-lcat.xlsx" },
  by_pop: { api: downloadMonthlyTMByPOPAPI, filename: "monthly-tm-hours-by-pop.xlsx" },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Month / scenario metadata                                     */
/* ══════════════════════════════════════════════════════════════ */
const TM_MONTHS = [
  { key: "jan", label: "Jan", scenario: "Actuals", isActual: true },
  { key: "feb", label: "Feb", scenario: "Actuals", isActual: true },
  { key: "mar", label: "Mar", scenario: "Actuals", isActual: true },
  { key: "apr", label: "Apr", scenario: "Actuals", isActual: true },
  { key: "may", label: "May", scenario: "Forecast", isActual: false },
  { key: "jun", label: "Jun", scenario: "Forecast", isActual: false },
  { key: "jul", label: "Jul", scenario: "Forecast", isActual: false },
  { key: "aug", label: "Aug", scenario: "Forecast", isActual: false },
  { key: "sep", label: "Sep", scenario: "Forecast", isActual: false },
  { key: "oct", label: "Oct", scenario: "Forecast", isActual: false },
  { key: "nov", label: "Nov", scenario: "Forecast", isActual: false },
  { key: "dec", label: "Dec", scenario: "Forecast", isActual: false },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — Total row                                              */
/* ══════════════════════════════════════════════════════════════ */
const TM_TOTAL = {
  jan: { hours: 77388.2, dollars: 8990053.5 },
  feb: { hours: 71533.3, dollars: 8339221.39 },
  mar: { hours: 84860.05, dollars: 9505202.29 },
  apr: { hours: 87737.55, dollars: 9195425.72 },
  may: { hours: 70493.36, dollars: 7618402.09 },
  jun: { hours: 72156.24, dollars: 7812345.67 },
  jul: { hours: 74832.6, dollars: 8105432.1 },
  aug: { hours: 71248.8, dollars: 7618402.09 },
  sep: { hours: 57426.53, dollars: 6371159.58 },
  oct: { hours: 52131.24, dollars: 5722832.67 },
  nov: { hours: 46894.5, dollars: 5134959.73 },
  dec: { hours: 53212.06, dollars: 5794005.81 },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Data — By Person rows                                         */
/* ══════════════════════════════════════════════════════════════ */
const Z = { hours: 0.0, dollars: 0.0 };

/* ══════════════════════════════════════════════════════════════ */
/*  Data — By LCAT groups (expandable)                           */
/* ══════════════════════════════════════════════════════════════ */
const TM_BY_LCAT_GROUPS = [
  {
    id: "0001",
    lcat: "0001",
    lcatDesc: "ADMINISTRATIVE ASSISTANT",
    billrate: 98.84,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "0001",
        lcatDesc: "Automated Test Architect",
        billrate: 96.9,
        jan: { hours: 328.0, dollars: 31783.2 },
        feb: { hours: 288.0, dollars: 27907.2 },
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
    ],
  },
  {
    id: "000101",
    lcat: "000101",
    lcatDesc: "",
    billrate: null,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000103",
    lcat: "000103",
    lcatDesc: "Application Engineer II",
    billrate: 124.13,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000104",
    lcat: "000104",
    lcatDesc: "Application Programmer II",
    billrate: 100.27,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000105",
    lcat: "000105",
    lcatDesc: "Database Managment Spec III",
    billrate: 121.94,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000108",
    lcat: "000108",
    lcatDesc: "Database Specialist III",
    billrate: 116.71,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000111",
    lcat: "000111",
    lcatDesc: "SME- Level III",
    billrate: 155.78,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "000111",
        lcatDesc: "SME- Level III",
        billrate: 155.81,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "000111",
        lcatDesc: "SME- Level III",
        billrate: 171.52,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
    ],
  },
  {
    id: "000115",
    lcat: "000115",
    lcatDesc: "Database Specialist- III",
    billrate: 171.52,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000116",
    lcat: "000116",
    lcatDesc: "Database Management Spec.- III",
    billrate: 171.52,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "000119",
    lcat: "000119",
    lcatDesc: "SME- Level I",
    billrate: 0.0,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "000119",
        lcatDesc: "SME- Level I",
        billrate: 188.24,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "000119",
        lcatDesc: "SME- Level I",
        billrate: 192.13,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
    ],
  },
  {
    id: "000123",
    lcat: "000123",
    lcatDesc: "App. Programmer II Level II",
    billrate: 173.75,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
  {
    id: "0002",
    lcat: "0002",
    lcatDesc: "Sr. Test Engineer",
    billrate: 88.64,
    jan: { hours: 160.0, dollars: 14182.4 },
    feb: { hours: 144.0, dollars: 12764.16 },
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "0002",
        lcatDesc: "TECH WRITER",
        billrate: 96.9,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "0002",
        lcatDesc: "TECH WRITER",
        billrate: 98.84,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
    ],
  },
  {
    id: "0003",
    lcat: "0003",
    lcatDesc: "SOFTWARE SUPERVISOR",
    billrate: 93.03,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "0003",
        lcatDesc: "Test Engineer",
        billrate: 85.95,
        jan: { hours: 320.0, dollars: 27504.0 },
        feb: { hours: 288.0, dollars: 24753.6 },
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
    ],
  },
  {
    id: "0004",
    lcat: "0004",
    lcatDesc: "PROJECT MANAGER 1",
    billrate: 117.3,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [
      {
        lcat: "0004",
        lcatDesc: "PROJECT MANAGER 1",
        billrate: 119.65,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "0004",
        lcatDesc: "Senior Developer",
        billrate: 117.3,
        jan: { hours: 952.0, dollars: 111669.6 },
        feb: { hours: 824.0, dollars: 96655.2 },
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
    ],
  },
  {
    id: "0008",
    lcat: "0008",
    lcatDesc: "NETWORK SECURITY ENG 1",
    billrate: 96.09,
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
    children: [],
  },
];

const TM_BY_PERSON = [
  {
    empId: "VCT01",
    empName: "SOMASUNDARAM, MALAR",
    projId: "06021.305.0003.00001",
    lcat: "SME3",
    jan: { hours: 160.0, dollars: 30118.4 },
    feb: { hours: 152.0, dollars: 28612.48 },
    mar: { hours: 184.0, dollars: 34636.16 },
    apr: { hours: 176.0, dollars: 33130.24 },
    may: { hours: 148.8, dollars: 27993.12 },
    jun: { hours: 156.24, dollars: 29373.12 },
    jul: { hours: 163.68, dollars: 30779.04 },
    aug: { hours: 156.24, dollars: 29403.09 },
    sep: { hours: 148.8, dollars: 28010.11 },
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "VCT01",
    empName: "SOMASUNDARAM, MALAR",
    projId: "06021.FOR",
    lcat: "000119",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: { hours: 0.0, dollars: null },
    jun: null,
    jul: null,
    aug: null,
    sep: Z,
    oct: null,
    nov: null,
    dec: null,
  },
  {
    empId: "V03600-002",
    empName: "Kay, Christopher",
    projId: "21804.000.001.001",
    lcat: "601018",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03600-002",
    empName: "Kay, Christopher",
    projId: "21804.000.001.002",
    lcat: "601018",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03600-002",
    empName: "Kay, Christopher",
    projId: "21804.000.001.003",
    lcat: "601018",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03600-001",
    empName: "Johnson, Rickey",
    projId: "21804.000.001.001",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03600-001",
    empName: "Johnson, Rickey",
    projId: "21804.000.001.002",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03600-001",
    empName: "Johnson, Rickey",
    projId: "21804.000.001.003",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-003",
    empName: "Nguyen, Vo",
    projId: "21804.000.001.001",
    lcat: "601165",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-003",
    empName: "Nguyen, Vo",
    projId: "21804.000.001.002",
    lcat: "601165",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-003",
    empName: "Nguyen, Vo",
    projId: "21804.000.001.003",
    lcat: "601165",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-002",
    empName: "Luong, Dong",
    projId: "21804.000.001.001",
    lcat: "601004",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-002",
    empName: "Luong, Dong",
    projId: "21804.000.001.002",
    lcat: "601004",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-002",
    empName: "Luong, Dong",
    projId: "21804.000.001.003",
    lcat: "601004",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-001",
    empName: "Le, Son",
    projId: "21804.000.001.001",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-001",
    empName: "Le, Son",
    projId: "21804.000.001.002",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03597-001",
    empName: "Le, Son",
    projId: "21804.000.001.003",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03594-001",
    empName: "Campbell, Chris",
    projId: "21804.000.001.001",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03594-001",
    empName: "Campbell, Chris",
    projId: "21804.000.001.002",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03594-001",
    empName: "Campbell, Chris",
    projId: "21804.000.001.003",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03592-003",
    empName: "Werntz, Dan",
    projId: "21804.000.001.001",
    lcat: "601004",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03592-002",
    empName: "Nguyen, Xuan",
    projId: "21804.000.001.001",
    lcat: "601013",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03592-001",
    empName: "Kirby, Jamal",
    projId: "21804.000.001.001",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03590-003",
    empName: "Sorensen, Brent",
    projId: "21804.000.001.001",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03590-003",
    empName: "Sorensen, Brent",
    projId: "21804.000.001.002",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03590-003",
    empName: "Sorensen, Brent",
    projId: "21804.000.001.003",
    lcat: "601006",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
  {
    empId: "V03590-002",
    empName: "Salahi, Arya",
    projId: "21804.000.001.001",
    lcat: "601004",
    jan: null,
    feb: null,
    mar: null,
    apr: null,
    may: Z,
    jun: Z,
    jul: Z,
    aug: Z,
    sep: Z,
    oct: Z,
    nov: Z,
    dec: Z,
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Data — By Period of Performance groups                        */
/* ══════════════════════════════════════════════════════════════ */
const TM_BY_POP_GROUPS = [
  {
    id: "01115_004",
    projId: "01115.004.001.0001.0001",
    rows: [
      {
        lcat: "122",
        lcatDesc: "JOURNEYMAN COMP & INFO SYS MGR",
        billrate: 130.63,
        jan: { hours: 8.0, dollars: 1045.04 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "131",
        lcatDesc: "JR COMPUTER HARDWARE ENGINEER",
        billrate: 95.13,
        jan: { hours: 8.0, dollars: 761.04 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "132",
        lcatDesc: "JOURNEYMAN COMP HARDWARE ENG",
        billrate: 132.2,
        jan: { hours: 8.0, dollars: 1057.6 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "142",
        lcatDesc: "JOURNEYMAN COMP NETWK ARCHITEC",
        billrate: 116.9,
        jan: { hours: 8.0, dollars: 935.2 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "143",
        lcatDesc: "SR COMPUTER NETWORK ARCHITECT",
        billrate: 138.92,
        jan: { hours: 8.0, dollars: 1111.36 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "152",
        lcatDesc: "JOURNEYMAN COMP NETWK SPT SPEC",
        billrate: 87.48,
        jan: { hours: 8.0, dollars: 699.84 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "192",
        lcatDesc: "JOURNEYMAN COMP SYS ENGINEER",
        billrate: 133.11,
        jan: { hours: 16.0, dollars: 2129.76 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "201",
        lcatDesc: "JR COMPUTER USER SPPT SPEC",
        billrate: 63.41,
        jan: { hours: 16.0, dollars: 1014.56 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "202",
        lcatDesc: "JOURNEYMAN COMP USER SPPT SPEC",
        billrate: 69.07,
        jan: { hours: 24.0, dollars: 1657.68 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "203A",
        lcatDesc: "SR COMPUTER USER SPPT SPEC",
        billrate: 81.25,
        jan: { hours: 24.0, dollars: 1950.0 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
      {
        lcat: "204",
        lcatDesc: "SME - COMPUTER USER SPPT SPEC",
        billrate: 91.91,
        jan: { hours: 24.0, dollars: 2205.84 },
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        jul: null,
        aug: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
      },
    ],
  },
  {
    id: "01115_005",
    projId: "01115.005.001",
    rows: [
      {
        lcat: "122",
        lcatDesc: "JOURNEYMAN COMP & INFO SYS MGR",
        billrate: 133.23,
        jan: { hours: 152.0, dollars: 20250.96 },
        feb: { hours: 144.0, dollars: 19185.12 },
        mar: { hours: 160.0, dollars: 21316.8 },
        apr: { hours: 136.0, dollars: 18119.28 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "131",
        lcatDesc: "JR COMPUTER HARDWARE ENGINEER",
        billrate: 97.03,
        jan: { hours: 144.0, dollars: 13972.32 },
        feb: { hours: 152.0, dollars: 14748.56 },
        mar: { hours: 160.0, dollars: 15524.8 },
        apr: { hours: 160.0, dollars: 15524.8 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "132",
        lcatDesc: "JOURNEYMAN COMP HARDWARE ENG",
        billrate: 134.84,
        jan: { hours: 147.0, dollars: 19821.48 },
        feb: { hours: 152.0, dollars: 20495.68 },
        mar: { hours: 172.0, dollars: 23192.48 },
        apr: { hours: 174.0, dollars: 23462.16 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "142",
        lcatDesc: "JOURNEYMAN COMP NETWK ARCHITEC",
        billrate: 119.25,
        jan: { hours: 152.0, dollars: 18126.0 },
        feb: { hours: 144.0, dollars: 17172.0 },
        mar: { hours: 176.0, dollars: 20988.0 },
        apr: { hours: 144.0, dollars: 17172.0 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "143",
        lcatDesc: "SR COMPUTER NETWORK ARCHITECT",
        billrate: 141.7,
        jan: { hours: 112.0, dollars: 15870.4 },
        feb: { hours: 152.0, dollars: 21538.4 },
        mar: { hours: 136.0, dollars: 19271.2 },
        apr: { hours: 176.0, dollars: 24939.2 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "152",
        lcatDesc: "JOURNEYMAN COMP NETWK SPT SPEC",
        billrate: 89.22,
        jan: { hours: 267.0, dollars: 23821.74 },
        feb: { hours: 256.0, dollars: 22840.32 },
        mar: { hours: 350.0, dollars: 31227.0 },
        apr: { hours: 350.0, dollars: 31227.0 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "192",
        lcatDesc: "JOURNEYMAN COMP SYS ENGINEER",
        billrate: 135.77,
        jan: { hours: 304.0, dollars: 41274.08 },
        feb: { hours: 292.5, dollars: 39712.73 },
        mar: { hours: 288.0, dollars: 39101.76 },
        apr: { hours: 340.0, dollars: 46161.8 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "201",
        lcatDesc: "JR COMPUTER USER SPPT SPEC",
        billrate: 64.68,
        jan: { hours: 296.0, dollars: 19145.28 },
        feb: { hours: 304.0, dollars: 19662.72 },
        mar: { hours: 390.0, dollars: 25225.2 },
        apr: { hours: 507.5, dollars: 32825.1 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "202",
        lcatDesc: "JOURNEYMAN COMP USER SPPT SPEC",
        billrate: 70.46,
        jan: { hours: 734.0, dollars: 51717.64 },
        feb: { hours: 638.0, dollars: 44953.48 },
        mar: { hours: 673.0, dollars: 47419.58 },
        apr: { hours: 639.6, dollars: 45059.32 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
      {
        lcat: "203",
        lcatDesc: "SR COMPUTER USER SPPT SPEC",
        billrate: 82.87,
        jan: { hours: 880.0, dollars: 72925.6 },
        feb: { hours: 849.5, dollars: 70398.07 },
        mar: { hours: 990.5, dollars: 82082.74 },
        apr: { hours: 982.0, dollars: 81378.34 },
        may: Z,
        jun: Z,
        jul: Z,
        aug: Z,
        sep: Z,
        oct: Z,
        nov: Z,
        dec: Z,
      },
    ],
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Helpers                                                       */
/* ══════════════════════════════════════════════════════════════ */
const fmtNum = (v) => {
  if (v === null || v === undefined) return "";
  return v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PL_PAGE_BG,
    backgroundImage: `
      radial-gradient(ellipse 90% 55% at 50% -5%, rgba(245,158,11,0.05) 0%, transparent 60%),
      radial-gradient(ellipse 50% 40% at 0% 100%, rgba(37,99,235,0.04) 0%, transparent 50%)
    `,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },

  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },

  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  headerContainer: {
    margin: "20px 0 0",
    padding: "0 16px",
    flexShrink: 0,
  },

  tabBar: {
    background: "#FFFFFF",
    borderBottom: "1px solid #E0E5EC",
    display: "flex",
    alignItems: "stretch",
    flexShrink: 0,
    overflowX: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  tab: {
    padding: "13px 18px",
    fontSize: 13,
    fontWeight: 500,
    color: "#64748B",
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: "none",
    border: "none",
    fontFamily: REPORT_FONT,
    transition: "color 0.15s, box-shadow 0.15s",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    "&:hover": { color: "#6366F1" },
  },
  tabActive: {
    color: "#6366F1",
    fontWeight: 600,
    boxShadow: "inset 0 -2px 0 0 #6366F1",
  },

  content: {
    flex: 1,
    padding: "0 16px",
    overflowY: "auto",
    minWidth: 0,
  },

  /* ── Horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── Active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  tableCard: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    overflow: "hidden",
    marginBottom: 24,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    animation: "$fadeUp 0.5s ease both",
  },
  tableSectionHead: {
    padding: "20px 24px 16px",
    borderBottom: "1px solid #E2E8F0",
    background: "#FFFFFF",
  },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: REPORT_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: "#6B7280",
    fontFamily: REPORT_FONT,
    lineHeight: 1.4,
  },
  tableWrap: { overflowX: "auto", background: "#FFFFFF" },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "12px",
    tableLayout: "auto",
  },

  /* ── header cells (4-row) ── */
  thBlank: {
    background: HEADER_BG,
    border: "none",
    padding: "7px 10px",
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  thYear: {
    background: HEADER_BG,
    color: "#fff",
    padding: "7px 12px",
    fontWeight: 700,
    fontSize: "12px",
    textAlign: "center",
    border: "none",
  },
  thMonthLabel: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontSize: "10px",
    fontWeight: 600,
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 405,
    zIndex: 3,
  },
  thMonth: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "12px",
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    minWidth: "120px",
  },
  thMonthFC: {
    background: HEADER_FC,
    color: "rgba(255,255,255,.85)",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "12px",
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    minWidth: "120px",
  },
  thScenarioLabel: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontSize: "10px",
    fontWeight: 600,
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 405,
    zIndex: 3,
  },
  thActuals: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    padding: "8px 10px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "center",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    border: "none",
    whiteSpace: "nowrap",
  },
  thForecast: {
    background: HEADER_FC,
    color: "#93C5FD",
    padding: "8px 10px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "center",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    border: "none",
    whiteSpace: "nowrap",
  },
  thFixed: {
    background: HEADER_FC,
    color: "rgba(255,255,255,.7)",
    border: "none",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    zIndex: 3,
  },
  thHours: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    border: "none",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    minWidth: "68px",
  },
  thHoursFC: {
    background: HEADER_FC,
    color: "rgba(255,255,255,.5)",
    border: "none",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    minWidth: "68px",
  },
  thDollars: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    border: "none",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    minWidth: "90px",
  },
  thDollarsFC: {
    background: HEADER_FC,
    color: "rgba(255,255,255,.5)",
    border: "none",
    padding: "7px 10px",
    fontWeight: 700,
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    minWidth: "90px",
  },

  /* ── total row ── */
  tdTotalLabel: {
    padding: "7px 10px",
    fontWeight: 700,
    color: "#111827",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #CBD5E1",
    fontSize: "12px",
    background: "#F0FFF4",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdTotalEmpty: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
  },
  tdTotalNum: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 600,
    color: "#111827",
    borderBottom: "1px solid #CBD5E1",
    fontSize: "12px",
    background: "#F0FFF4",
    whiteSpace: "nowrap",
  },

  /* ── employee row cells ── */
  tdEmpId: {
    padding: "7px 10px",
    fontWeight: 600,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdText: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
  },
  tdNum: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  tdEmpName: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 90,
    zIndex: 1,
  },
  tdProjId: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 250,
    zIndex: 1,
  },
  tdTotalEmpName: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 90,
    zIndex: 1,
  },
  tdTotalProjId: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 250,
    zIndex: 1,
  },
  tdLcat: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 405,
    zIndex: 1,
    boxShadow: "4px 0 6px -2px rgba(0,0,0,0.08)",
  },
  tdTotalLcat: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 405,
    zIndex: 1,
  },

  /* ── By LCAT sticky cols ── */
  tdLcatCode: {
    padding: "7px 10px",
    fontWeight: 600,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdLcatDesc: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 90,
    zIndex: 1,
    minWidth: "260px",
  },
  tdBillrate: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 350,
    zIndex: 1,
    minWidth: "80px",
  },
  tdTotalLcatDesc: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 90,
    zIndex: 1,
  },
  tdTotalBillrate: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 350,
    zIndex: 1,
  },

  /* ── By POP sticky cols ── */
  tdPOPProjId: {
    padding: "7px 10px",
    fontWeight: 600,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 0,
    zIndex: 1,
    minWidth: "155px",
  },
  tdPOPLcat: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 155,
    zIndex: 1,
    minWidth: "55px",
  },
  tdPOPLcatDesc: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 210,
    zIndex: 1,
    minWidth: "240px",
  },
  tdPOPBillrate: {
    padding: "7px 10px",
    fontWeight: 400,
    color: "#334155",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: "12px",
    background: "#fff",
    position: "sticky",
    left: 450,
    zIndex: 1,
    minWidth: "75px",
  },
  tdTotalPOPLcat: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 155,
    zIndex: 1,
  },
  tdTotalPOPLcatDesc: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 210,
    zIndex: 1,
  },
  tdTotalPOPBillrate: {
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    position: "sticky",
    left: 450,
    zIndex: 1,
  },

  /* ── placeholder ── */
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 24px",
    color: "#94A3B8",
    gap: "12px",
    background: "#fff",
    borderRadius: "0 0 12px 12px",
  },
  placeholderTitle: { fontSize: "16px", fontWeight: 600, color: "#64748B" },
  placeholderSub: { fontSize: "13px" },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ── Horizontal filter bar component ─────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  By Person tab                                                 */
/* ══════════════════════════════════════════════════════════════ */
function ByPersonTab({ classes }) {
  const totalCols = TM_MONTHS.length * 2; /* Hours + Dollars per month */

  const cellBg = (isActual) => ({ background: isActual ? "#fff" : "#FAFBFD" });

  return (
    <div>
      <div className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              {/* ── Row 1: Year / 2026 band ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th className={classes.thMonthLabel}>Year</th>
                <th className={classes.thYear} colSpan={totalCols}>
                  2026
                </th>
              </tr>

              {/* ── Row 2: MonthName + month labels ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th className={classes.thMonthLabel}>MonthName</th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={m.isActual ? classes.thMonth : classes.thMonthFC}
                  >
                    {m.label}
                  </th>
                ))}
              </tr>

              {/* ── Row 3: Scenario labels ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th className={classes.thScenarioLabel}>Scenario</th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={
                      m.isActual ? classes.thActuals : classes.thForecast
                    }
                  >
                    {m.scenario}
                  </th>
                ))}
              </tr>

              {/* ── Row 4: Column names ── */}
              <tr>
                <th
                  className={classes.thFixed}
                  style={{ left: 0, minWidth: "90px" }}
                >
                  Emp ID
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 90, minWidth: "160px" }}
                >
                  Employee Name
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 250, minWidth: "155px" }}
                >
                  Proj_ID
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 405, minWidth: "70px" }}
                >
                  LCAT
                </th>
                {TM_MONTHS.map((m) => (
                  <React.Fragment key={m.key}>
                    <th
                      className={
                        m.isActual ? classes.thHours : classes.thHoursFC
                      }
                    >
                      Hours
                    </th>
                    <th
                      className={
                        m.isActual ? classes.thDollars : classes.thDollarsFC
                      }
                    >
                      Dollars
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ── Total row ── */}
              <tr>
                <td className={classes.tdTotalLabel}>Total</td>
                <td className={classes.tdTotalEmpName} />
                <td className={classes.tdTotalProjId} />
                <td className={classes.tdTotalLcat} />
                {TM_MONTHS.map((m) => {
                  const d = TM_TOTAL[m.key];
                  return (
                    <React.Fragment key={m.key}>
                      <td className={classes.tdTotalNum}>{fmtNum(d?.hours)}</td>
                      <td className={classes.tdTotalNum}>
                        {fmtNum(d?.dollars)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Employee rows ── */}
              {TM_BY_PERSON.map((row, i) => (
                <tr
                  key={i}
                  style={{ background: i % 2 === 0 ? "#fff" : "#F4F6F9" }}
                >
                  <td
                    className={classes.tdEmpId}
                    style={{ background: "inherit" }}
                  >
                    {row.empId}
                  </td>
                  <td
                    className={classes.tdEmpName}
                    style={{ background: "inherit" }}
                  >
                    {row.empName}
                  </td>
                  <td
                    className={classes.tdProjId}
                    style={{ background: "inherit" }}
                  >
                    {row.projId}
                  </td>
                  <td
                    className={classes.tdLcat}
                    style={{ background: "inherit" }}
                  >
                    {row.lcat}
                  </td>
                  {TM_MONTHS.map((m) => {
                    const d = row[m.key];
                    return (
                      <React.Fragment key={m.key}>
                        <td
                          className={classes.tdNum}
                          style={{ background: "inherit" }}
                        >
                          {fmtNum(d?.hours)}
                        </td>
                        <td
                          className={classes.tdNum}
                          style={{ background: "inherit" }}
                        >
                          {fmtNum(d?.dollars)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Placeholder tab                                               */
/* ══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════ */
/*  By LCAT tab                                                   */
/* ══════════════════════════════════════════════════════════════ */
function ByLcatTab({ classes }) {
  const totalCols = TM_MONTHS.length * 2;
  const LCAT_LABEL_LEFT = 350; /* left offset of Billrate = where Year/MonthName/Scenario sits */

  const [expanded, setExpanded] = useState(() => {
    const ids = new Set();
    TM_BY_LCAT_GROUPS.forEach((g) => {
      if (g.children.length > 0) ids.add(g.id);
    });
    return ids;
  });

  const [hiddenBillrates, setHiddenBillrates] = useState(new Set());

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleBillrate = (id) => {
    setHiddenBillrates((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const fmtBr = (v) => (v === null || v === undefined ? "" : v.toFixed(2));

  return (
    <div>
      <div className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              {/* ── Row 1: Year / 2026 ── */}
              <tr>
                <th className={classes.thBlank} colSpan={2} />
                <th
                  className={classes.thMonthLabel}
                  style={{ left: LCAT_LABEL_LEFT }}
                >
                  Year
                </th>
                <th className={classes.thYear} colSpan={totalCols}>
                  2026
                </th>
              </tr>

              {/* ── Row 2: MonthName ── */}
              <tr>
                <th className={classes.thBlank} colSpan={2} />
                <th
                  className={classes.thMonthLabel}
                  style={{ left: LCAT_LABEL_LEFT }}
                >
                  MonthName
                </th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={m.isActual ? classes.thMonth : classes.thMonthFC}
                  >
                    {m.label}
                  </th>
                ))}
              </tr>

              {/* ── Row 3: Scenario ── */}
              <tr>
                <th className={classes.thBlank} colSpan={2} />
                <th
                  className={classes.thScenarioLabel}
                  style={{ left: LCAT_LABEL_LEFT }}
                >
                  Scenario
                </th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={
                      m.isActual ? classes.thActuals : classes.thForecast
                    }
                  >
                    {m.scenario}
                  </th>
                ))}
              </tr>
              <tr>
                <th
                  className={classes.thFixed}
                  style={{ left: 0, minWidth: "90px" }}
                >
                  LCAT
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 90, minWidth: "260px" }}
                >
                  Lcat_Desc
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 350, minWidth: "80px", textAlign: "right" }}
                >
                  Billrate
                </th>
                {TM_MONTHS.map((m) => (
                  <React.Fragment key={m.key}>
                    <th
                      className={
                        m.isActual ? classes.thHours : classes.thHoursFC
                      }
                    >
                      Hours
                    </th>
                    <th
                      className={
                        m.isActual ? classes.thDollars : classes.thDollarsFC
                      }
                    >
                      Dollars
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ── Total row ── */}
              <tr>
                <td className={classes.tdTotalLabel}>Total</td>
                <td className={classes.tdTotalLcatDesc} />
                <td className={classes.tdTotalBillrate} />
                {TM_MONTHS.map((m) => {
                  const d = TM_TOTAL[m.key];
                  return (
                    <React.Fragment key={m.key}>
                      <td className={classes.tdTotalNum}>{fmtNum(d?.hours)}</td>
                      <td className={classes.tdTotalNum}>
                        {fmtNum(d?.dollars)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── LCAT groups ── */}
              {TM_BY_LCAT_GROUPS.map((group) => (
                <React.Fragment key={group.id}>
                  {/* Parent / group row */}
                  <tr style={{ background: "#EEF2FA" }}>
                    <td
                      className={classes.tdLcatCode}
                      style={{ background: "#EEF2FA" }}
                    >
                      {group.children.length > 0 && (
                        <span
                          onClick={() => toggle(group.id)}
                          style={{
                            cursor: "pointer",
                            marginRight: "5px",
                            fontSize: "13px",
                            color: NAV,
                            userSelect: "none",
                          }}
                        >
                          {expanded.has(group.id) ? "⊖" : "⊕"}
                        </span>
                      )}
                      <strong>{group.lcat}</strong>
                    </td>
                    <td
                      className={classes.tdLcatDesc}
                      style={{
                        background: "#EEF2FA",
                        fontWeight: 600,
                        color: NAV,
                      }}
                    >
                      {group.children.length > 0 && (
                        <span
                          onClick={() => toggleBillrate(group.id)}
                          style={{
                            cursor: "pointer",
                            marginRight: "5px",
                            fontSize: "13px",
                            color: NAV,
                            userSelect: "none",
                          }}
                        >
                          {hiddenBillrates.has(group.id) ? "⊕" : "⊖"}
                        </span>
                      )}
                      {group.lcatDesc}
                    </td>
                    <td
                      className={classes.tdBillrate}
                      style={{ background: "#EEF2FA", fontWeight: 600 }}
                    >
                      {hiddenBillrates.has(group.id)
                        ? ""
                        : fmtBr(group.billrate)}
                    </td>
                    {TM_MONTHS.map((m) => {
                      const d = group[m.key];
                      return (
                        <React.Fragment key={m.key}>
                          <td
                            className={classes.tdNum}
                            style={{ background: "#EEF2FA" }}
                          >
                            {fmtNum(d?.hours)}
                          </td>
                          <td
                            className={classes.tdNum}
                            style={{ background: "#EEF2FA" }}
                          >
                            {fmtNum(d?.dollars)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>

                  {/* Child rows */}
                  {expanded.has(group.id) &&
                    group.children.map((child, ci) => (
                      <tr
                        key={`${group.id}-${ci}`}
                        style={{
                          background: ci % 2 === 0 ? "#fff" : "#F4F6F9",
                        }}
                      >
                        <td
                          className={classes.tdLcatCode}
                          style={{ background: "inherit" }}
                        >
                          {child.lcat}
                        </td>
                        <td
                          className={classes.tdLcatDesc}
                          style={{ background: "inherit" }}
                        >
                          {child.lcatDesc}
                        </td>
                        <td
                          className={classes.tdBillrate}
                          style={{ background: "inherit" }}
                        >
                          {fmtBr(child.billrate)}
                        </td>
                        {TM_MONTHS.map((m) => {
                          const d = child[m.key];
                          return (
                            <React.Fragment key={m.key}>
                              <td
                                className={classes.tdNum}
                                style={{ background: "inherit" }}
                              >
                                {fmtNum(d?.hours)}
                              </td>
                              <td
                                className={classes.tdNum}
                                style={{ background: "inherit" }}
                              >
                                {fmtNum(d?.dollars)}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  By Period of Performance tab                                  */
/* ══════════════════════════════════════════════════════════════ */
function ByPOPTab({ classes }) {
  const totalCols = TM_MONTHS.length * 2;
  const POP_LBL_LEFT = 450;

  const [expandedPOPs, setExpandedPOPs] = useState(
    () => new Set(TM_BY_POP_GROUPS.map((g) => g.id)),
  );
  const [hiddenPOPLcatDescs, setHiddenPOPLcatDescs] = useState(new Set());
  const [hiddenPOPBillrates, setHiddenPOPBillrates] = useState(new Set());

  const togglePOP = (id) =>
    setExpandedPOPs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const togglePOPLcatDesc = (key) =>
    setHiddenPOPLcatDescs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const togglePOPBillrate = (key) =>
    setHiddenPOPBillrates((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const fmtBr = (v) => (v === null || v === undefined ? "" : v.toFixed(2));

  return (
    <div>
      <div className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              {/* ── Row 1: Year / 2026 ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th
                  className={classes.thMonthLabel}
                  style={{ left: POP_LBL_LEFT }}
                >
                  Year
                </th>
                <th className={classes.thYear} colSpan={totalCols}>
                  2026
                </th>
              </tr>

              {/* ── Row 2: MonthName ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th
                  className={classes.thMonthLabel}
                  style={{ left: POP_LBL_LEFT }}
                >
                  MonthName
                </th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={m.isActual ? classes.thMonth : classes.thMonthFC}
                  >
                    {m.label}
                  </th>
                ))}
              </tr>

              {/* ── Row 3: Scenario ── */}
              <tr>
                <th className={classes.thBlank} colSpan={3} />
                <th
                  className={classes.thScenarioLabel}
                  style={{ left: POP_LBL_LEFT }}
                >
                  Scenario
                </th>
                {TM_MONTHS.map((m) => (
                  <th
                    key={m.key}
                    colSpan={2}
                    className={
                      m.isActual ? classes.thActuals : classes.thForecast
                    }
                  >
                    {m.scenario}
                  </th>
                ))}
              </tr>

              {/* ── Row 4: Column names ── */}
              <tr>
                <th
                  className={classes.thFixed}
                  style={{ left: 0, minWidth: "155px" }}
                >
                  Proj_Id
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 155, minWidth: "55px" }}
                >
                  LCAT
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 210, minWidth: "240px" }}
                >
                  Lcat_Desc
                </th>
                <th
                  className={classes.thFixed}
                  style={{ left: 450, minWidth: "75px", textAlign: "right" }}
                >
                  Billrate
                </th>
                {TM_MONTHS.map((m) => (
                  <React.Fragment key={m.key}>
                    <th
                      className={
                        m.isActual ? classes.thHours : classes.thHoursFC
                      }
                    >
                      Hours
                    </th>
                    <th
                      className={
                        m.isActual ? classes.thDollars : classes.thDollarsFC
                      }
                    >
                      Dollars
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ── Total row ── */}
              <tr>
                <td className={classes.tdTotalLabel}>Total</td>
                <td className={classes.tdTotalPOPLcat} />
                <td className={classes.tdTotalPOPLcatDesc} />
                <td className={classes.tdTotalPOPBillrate} />
                {TM_MONTHS.map((m) => {
                  const d = TM_TOTAL[m.key];
                  return (
                    <React.Fragment key={m.key}>
                      <td className={classes.tdTotalNum}>{fmtNum(d?.hours)}</td>
                      <td className={classes.tdTotalNum}>
                        {fmtNum(d?.dollars)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── POP groups ── */}
              {TM_BY_POP_GROUPS.map((group) => {
                const isExp = expandedPOPs.has(group.id);
                return group.rows.map((row, ri) => {
                  if (!isExp && ri > 0) return null;
                  const brKey = `${group.id}-${ri}`;
                  const brHidden = hiddenPOPBillrates.has(brKey);
                  return (
                    <tr
                      key={brKey}
                      style={{ background: ri % 2 === 0 ? "#fff" : "#F4F6F9" }}
                    >
                      {/* Proj_Id */}
                      <td
                        className={classes.tdPOPProjId}
                        style={{ background: "inherit" }}
                      >
                        {ri === 0 && (
                          <span
                            onClick={() => togglePOP(group.id)}
                            style={{
                              cursor: "pointer",
                              marginRight: "5px",
                              fontSize: "13px",
                              color: NAV,
                              userSelect: "none",
                            }}
                          >
                            {isExp ? "⊖" : "⊕"}
                          </span>
                        )}
                        {group.projId}
                      </td>

                      {/* LCAT — toggle Lcat_Desc */}
                      <td
                        className={classes.tdPOPLcat}
                        style={{ background: "inherit" }}
                      >
                        <span
                          onClick={() => togglePOPLcatDesc(brKey)}
                          style={{
                            cursor: "pointer",
                            marginRight: "4px",
                            fontSize: "12px",
                            color: NAV,
                            userSelect: "none",
                          }}
                        >
                          {hiddenPOPLcatDescs.has(brKey) ? "⊕" : "⊖"}
                        </span>
                        {row.lcat}
                      </td>

                      {/* Lcat_Desc — toggle billrate */}
                      <td
                        className={classes.tdPOPLcatDesc}
                        style={{ background: "inherit" }}
                      >
                        {!hiddenPOPLcatDescs.has(brKey) && (
                          <span
                            onClick={() => togglePOPBillrate(brKey)}
                            style={{
                              cursor: "pointer",
                              marginRight: "5px",
                              fontSize: "13px",
                              color: NAV,
                              userSelect: "none",
                            }}
                          >
                            {brHidden ? "⊕" : "⊖"}
                          </span>
                        )}
                        {hiddenPOPLcatDescs.has(brKey) ? "" : row.lcatDesc}
                      </td>

                      {/* Billrate */}
                      <td
                        className={classes.tdPOPBillrate}
                        style={{ background: "inherit" }}
                      >
                        {brHidden ? "" : fmtBr(row.billrate)}
                      </td>

                      {/* Month data */}
                      {TM_MONTHS.map((m) => {
                        const d = row[m.key];
                        return (
                          <React.Fragment key={m.key}>
                            <td
                              className={classes.tdNum}
                              style={{ background: "inherit" }}
                            >
                              {fmtNum(d?.hours)}
                            </td>
                            <td
                              className={classes.tdNum}
                              style={{ background: "inherit" }}
                            >
                              {fmtNum(d?.dollars)}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PlaceholderTab({ subtitle, classes }) {
  return (
    <div>
      <div className={classes.placeholder}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#CBD5E1"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
        <span className={classes.placeholderTitle}>{subtitle}</span>
        <span className={classes.placeholderSub}>Content coming soon</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function MonthlyTMHours() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [personMeta, setPersonMeta] = useState(null);
  const [lcatMeta, setLcatMeta] = useState(null);
  const [popMeta, setPopMeta] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
    setFilterOpen(false);
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const actualsThroughLabel = (meta) => {
    const m = Number(meta?.actualThroughMonth);
    const year = new Date().getFullYear();
    if (!m || m < 1 || m > 12) return `Actuals through May ${year}`;
    return `Actuals through ${MONTH_NAMES[m - 1]} ${year}`;
  };

  const TAB_HEADER = {
    by_person: {
      eyebrow: "Monthly T&M Hours",
      title: "By Person",
      accent: "Hours & Dollars",
      badge: "Live Forecast",
      date: actualsThroughLabel(personMeta),
    },
    by_lcat: {
      eyebrow: "Monthly T&M Hours",
      title: "By LCAT",
      accent: "Hours & Dollars by LCAT",
      badge: "Live Forecast",
      date: actualsThroughLabel(lcatMeta),
    },
    by_pop: {
      eyebrow: "Monthly T&M Hours",
      title: "By Period of Performance",
      accent: "Hours & Dollars by POP",
      badge: "Live Forecast",
      date: actualsThroughLabel(popMeta),
    },
  };

  const renderContent = () => {
    if (activeTab === "by_person")
      return <ByPersonTabComponent filters={effectiveFilters} onMeta={setPersonMeta} />;
    if (activeTab === "by_lcat")
      return <ByLcat filters={effectiveFilters} onMeta={setLcatMeta} />;
    if (activeTab === "by_pop")
      return <ByPOPTabComponent filters={effectiveFilters} onMeta={setPopMeta} />;
    return null;
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await entry.api(effectiveFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Monthly T&M Hours download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Navigation bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Monthly T&amp;M Hours</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/monthly_tm_hours/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={
                    f.key === "year" &&
                    String(val) === String(new Date().getFullYear())
                  }
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero page header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>{renderContent()}</div>
      </div>
    </div>
  );
}
